/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    ActionPostHook,
    ActionPostReaderHook,
    ActionPreHook,
    ActionPreReaderHook,
    IAnyAction,
    msdyn365Commerce
} from '@msdyn365-commerce/core-internal';
import { LogLevel, StaticTelemetry } from '@msdyn365-commerce/telemetry-internal';

const DEPRECATED_HOOK_TYPES = ['pre', 'post', 'preRead', 'postRead'];

export namespace ActionRegistrar {
    type ActionHook<TOut = unknown> = ActionPreHook | ActionPostHook<TOut>;

    export type ActionRegistration = {
        action?: IAnyAction<unknown>;
        pre?: Set<ActionPreHook>;
        post?: Set<ActionPostHook<unknown>>;
        preRead?: Set<ActionPreHook>;
        postRead?: Set<ActionPostHook<unknown>>;
        preReader?: Set<ActionPreReaderHook>;
        postReader?: Set<ActionPostReaderHook<unknown>>;
    };

    interface IActionDictionary {
        [actionKey: string]: ActionRegistration;
    }

    const dataActionRegister: IActionDictionary = {};

    export const getRegistration = (id?: string): ActionRegistration | undefined => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if ((<any>msdyn365Commerce).getPartnerOverrideAction && id) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const partnerDataAction = (<any>msdyn365Commerce).getPartnerOverrideAction(id);
            if (partnerDataAction && partnerDataAction.default) {
                dataActionRegister[id].action = partnerDataAction.default;
            } else if (partnerDataAction && partnerDataAction.prototype && partnerDataAction.prototype.Action) {
                dataActionRegister[id].action = partnerDataAction;
            }
        }

        if (id) {
            return dataActionRegister[id];
        }

        return undefined;
    };

    export const unwrapAction = (action: IAnyAction<unknown>): IAnyAction<unknown> => {
        if (action && action.prototype && !action.prototype.isAction) {
            // registered action is an action wrapper. Unwrap it.
            return <IAnyAction<unknown>>action.prototype.Action;
        }
        return action;
    };
    /**
     * Gets the action registered with id
     * @param id - ID to look up action
     */
    export const getAction = <TOut>(id?: string): IAnyAction<TOut> | undefined => {
        if (!id) {
            return;
        }
        const registeredAction = getRegistration(id);
        if (registeredAction) {
            return <IAnyAction<TOut>>registeredAction.action;
        }
        return;
    };

    /**
     * registers action. Does nothing if action is already registered
     * @param id - ID to register action with
     * @param action - action to register
     */
    export const registerAction = <T>(id: string, dataAction: IAnyAction<T>): IAnyAction<T> | undefined => {
        if (!id || !dataAction) {
            // unable to register no id or action
            return;
        }

        const actionRegister = dataActionRegister;

        if (actionRegister[id]) {
            const registration = actionRegister[id];
            registration.action = dataAction;
            return <IAnyAction<T>>actionRegister[id].action;
        } else {
            const registration = { action: dataAction };
            actionRegister[id] = registration;
            return dataAction;
        }
    };

    const registerHook = <HookType = ActionHook | ActionHook[]>(
        hookType: 'pre' | 'post' | 'preRead' | 'postRead' | 'preReader' | 'postReader'
    ) => (id: string, hook: HookType) => {
        // deprecate read only hook
        if (!msdyn365Commerce.isBrowser && DEPRECATED_HOOK_TYPES.indexOf(hookType) > -1) {
            StaticTelemetry.log(
                LogLevel.Warning,
                `Data action hooks are deprecated and will be removed in later versions. Please use data action overrides to extend data actions. The related action of this ${hookType} hook is - ${id}`
            );
        }

        const registration = getRegistration(id);
        // coerce hook into an iterable
        const hooks = Array.isArray(hook) ? hook : [hook];
        if (!registration) {
            // If the registration does not exist init one with just the hooks
            const newRegistration: { [id: string]: unknown } = {};
            newRegistration[hookType] = new Set<ActionHook>(<ActionHook[]>hooks);
            dataActionRegister[id] = newRegistration;
        } else {
            // If the registration does exist merge new hooks with existing hooks
            const hookRegistration = registration[hookType] || new Set<ActionHook>();
            hooks.forEach(h => {
                hookRegistration.add(<ActionHook>h);
            });
            // @ts-ignore
            registration[hookType] = hookRegistration;
        }
        return hook;
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    export const registerPostHook = registerHook<ActionPostHook<any> | ActionPostHook<any>[]>('post');
    export const registerPreHook = registerHook<ActionPreHook | ActionPreHook[]>('pre');
    export const registerPreReadOnlyHook = registerHook<ActionPreHook | ActionPreHook[]>('preRead');
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    export const registerPostReadOnlyHook = registerHook<ActionPostHook<any> | ActionPostHook<any>[]>('postRead');
    export const registerPreReaderHook = registerHook<ActionPreReaderHook | ActionPreReaderHook[]>('preReader');
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    export const registerPostReaderHook = registerHook<ActionPostReaderHook<any> | ActionPostReaderHook<any>[]>('postReader');

    export const getPreHooks = (id: string) => {
        const actionRegistration = getRegistration(id);
        return actionRegistration && actionRegistration.pre;
    };

    export const getPostHooks = (id: string) => {
        const actionRegistration = getRegistration(id);
        return actionRegistration && actionRegistration.post;
    };

    export const getPreReadHooks = (id: string) => {
        const actionRegistration = getRegistration(id);
        return actionRegistration && actionRegistration.preRead;
    };

    export const getPostReadHooks = (id: string) => {
        const actionRegistration = getRegistration(id);
        return actionRegistration && actionRegistration.postRead;
    };

    export const getPreReaderHooks = (id: string) => {
        const actionRegistration = getRegistration(id);
        return actionRegistration && actionRegistration.preReader;
    };
    export const getPostReaderHooks = (id: string) => {
        const actionRegistration = getRegistration(id);
        return actionRegistration && actionRegistration.postReader;
    };

    /**
     * Resolves the action and returns first action in action chain. Registers action if not yet registered
     * @param id - Action id to look up or register under
     * @param action - Action function
     */
    export const resolveAction = <T>(id: string, action: IAnyAction<T>): IAnyAction<T> | undefined => {
        return getAction(id) || <IAnyAction<T>>registerAction(id, action) || action;
    };

    export const resolveRegistration = (
        id: string,
        action: IAnyAction<unknown>,
        preHooks?: ActionPreHook[],
        postHooks?: ActionPostHook<unknown>[],
        preReadHooks?: ActionPreHook[],
        postReadHooks?: ActionPostHook<unknown>[],
        preReaderHooks?: ActionPreReaderHook[],
        postReaderHooks?: ActionPostReaderHook<unknown>[]
    ) => {
        const resolvedAction = resolveAction(id, action);
        if (!resolvedAction) {
            return;
        }
        preHooks && registerPreHook(id, preHooks);
        postHooks && registerPostHook(id, postHooks);
        preReadHooks && registerPreReadOnlyHook(id, preReadHooks);
        postReadHooks && registerPostReadOnlyHook(id, postReadHooks);
        preReaderHooks && registerPreReaderHook(id, preReaderHooks);
        postReaderHooks && registerPostReaderHook(id, postReaderHooks);
        return getRegistration(id);
    };
}
